import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, X, Sparkles } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { RichTextEditor } from '../../ui/rich-text-editor';
import { MediaPicker } from './MediaPicker';
import { ICON_NAMES, ICON_MAP } from '../../website/sections/InfoSections';

// Field editors driven by the backend section registry (no code knowledge needed by admins).

export const ImageField = ({ api, value, onChange, label, context }) => {
  const [open, setOpen] = useState(false);
  const [suggest, setSuggest] = useState(null); // { alt, decorative } | 'loading'
  const [flash, setFlash] = useState(false);
  const ref = useRef(null);

  // AI alt-text suggestion: editable, nothing saved until the admin applies it and saves/publishes the draft.
  const suggestAlt = useCallback(() => {
    if (!value?.url) return;
    setSuggest('loading');
    api.ai('alt-text', { image_url: value.url, page_name: context?.pageName, section_type: context?.sectionType, context: context?.text })
      .then((r) => setSuggest({ alt: r.alt, decorative: r.decorative })).catch(() => { setSuggest(null); toast.error('Could not generate alt text'); });
  }, [api, value?.url, context]);

  // Health "Fix" deep link: scroll to this image and pre-generate a suggestion when it's the one flagged.
  useEffect(() => {
    const onFocus = (e) => {
      if (!value?.url || e.detail?.imageUrl !== value.url) return;
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlash(true); setTimeout(() => setFlash(false), 2500);
      suggestAlt();
    };
    window.addEventListener('builder-focus-image', onFocus);
    return () => window.removeEventListener('builder-focus-image', onFocus);
  }, [value?.url, suggestAlt]);

  return (
    <div data-testid={`image-field-${label}`} ref={ref} className={flash ? 'ring-2 ring-amber-400 ring-offset-2 transition-shadow' : ''}>
      <Label className="text-xs">{label}</Label>
      {value?.url ? (
        <div className="mt-1 flex gap-3">
          <img src={value.url} alt={value.alt_text || ''} className="w-24 h-20 object-cover border border-neutral-200" style={{ objectPosition: value.focal }} />
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <Input className="h-8 text-xs" placeholder={value.decorative ? 'Decorative — no alt text needed' : 'Alt text (describe the image)'} value={value.decorative ? '' : (value.alt_text || '')} disabled={!!value.decorative} onChange={(e) => onChange({ ...value, alt_text: e.target.value })} data-testid="image-alt-input" />
              {!value.decorative && <button type="button" onClick={suggestAlt} disabled={suggest === 'loading'} className="text-[11px] inline-flex items-center gap-1 text-amber-700 hover:underline whitespace-nowrap disabled:opacity-50" data-testid="image-alt-ai"><Sparkles className="w-3 h-3" />{suggest === 'loading' ? 'Thinking…' : 'Suggest'}</button>}
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-neutral-600 cursor-pointer"><input type="checkbox" checked={!!value.decorative} onChange={(e) => { onChange({ ...value, decorative: e.target.checked }); setSuggest(null); }} data-testid="image-decorative-toggle" />Decorative image (renders with empty alt, excluded from alt-text checks)</label>
            {suggest && suggest !== 'loading' && (
              <div className="border border-amber-200 bg-amber-50 p-2 text-xs space-y-1" data-testid="image-alt-suggestion">
                <p className="text-[10px] uppercase tracking-wide text-amber-700">AI suggestion — edit before applying</p>
                {suggest.decorative ? <p className="text-neutral-700">This looks decorative. Mark it as decorative instead of adding alt text?</p>
                  : <Input className="h-8 text-xs bg-white" value={suggest.alt} onChange={(e) => setSuggest({ ...suggest, alt: e.target.value })} data-testid="image-alt-suggestion-input" />}
                <div className="flex gap-3">
                  <button type="button" className="font-medium text-amber-800 underline" onClick={() => { onChange(suggest.decorative ? { ...value, decorative: true } : { ...value, alt_text: suggest.alt, decorative: false }); setSuggest(null); }} data-testid="image-alt-apply">{suggest.decorative ? 'Mark decorative' : 'Apply'}</button>
                  <button type="button" className="text-neutral-500 underline" onClick={() => setSuggest(null)} data-testid="image-alt-dismiss">Dismiss</button>
                </div>
              </div>
            )}
            <p className="text-[10px] text-neutral-400">{value.aspect ? `Crop ${value.aspect}` : 'Original ratio'}{value.focal ? ` · focus ${value.focal}` : ''}</p>
            <div className="flex gap-2 text-xs"><button className="underline" onClick={() => setOpen(true)}>Replace / crop</button><button className="underline text-red-600" onClick={() => onChange(null)}>Remove</button></div>
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="mt-1 w-full border border-dashed border-neutral-300 py-5 text-sm text-neutral-500 hover:border-neutral-500 flex items-center justify-center gap-2" data-testid="image-choose-btn"><ImageIcon className="w-4 h-4" />Choose image</button>
      )}
      <MediaPicker api={api} open={open} onClose={() => setOpen(false)} onSelect={onChange} initial={value} />
    </div>
  );
};

export const ButtonField = ({ value, onChange, label, pages = [] }) => {
  const v = value || {};
  return (
    <div className="border border-neutral-200 p-3 space-y-2" data-testid={`button-field-${label}`}>
      <Label className="text-xs">{label}</Label>
      <Input className="h-8" placeholder="Button text" value={v.text || ''} onChange={(e) => onChange({ ...v, text: e.target.value })} />
      <div className="flex gap-2">
        <Input className="h-8 flex-1" placeholder="Link (e.g. /quote or https://…)" value={v.link || ''} onChange={(e) => onChange({ ...v, link: e.target.value })} list={`pages-${label}`} />
        <datalist id={`pages-${label}`}>{pages.map((p) => <option key={p.slug} value={p.path}>{p.name}</option>)}</datalist>
      </div>
      <select className="w-full border border-neutral-300 h-8 text-sm bg-white px-2" value={v.style || 'primary'} onChange={(e) => onChange({ ...v, style: e.target.value })}>
        <option value="primary">Solid</option><option value="secondary">Outline</option><option value="accent">Accent color</option><option value="link">Text link</option>
      </select>
    </div>
  );
};

export const IconField = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const C = ICON_MAP[value] || ICON_MAP.Award;
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 border border-neutral-300 h-8 px-2 text-sm w-full bg-white" data-testid="icon-field-btn"><C className="w-4 h-4" />{value || 'Choose icon'}</button>
      {open && (
        <div className="absolute z-20 mt-1 bg-white border border-neutral-200 shadow-lg p-2 grid grid-cols-8 gap-1 w-72">
          {ICON_NAMES.map((n) => { const I = ICON_MAP[n]; return <button key={n} title={n} onClick={() => { onChange(n); setOpen(false); }} className={`p-1.5 hover:bg-neutral-100 ${n === value ? 'bg-amber-100' : ''}`}><I className="w-4 h-4" /></button>; })}
        </div>
      )}
    </div>
  );
};

export const ItemsField = ({ api, field, value = [], onChange, context }) => {
  const items = value || [];
  const update = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const move = (i, d) => { const n = [...items]; const [it] = n.splice(i, 1); n.splice(i + d, 0, it); onChange(n); };
  return (
    <div data-testid={`items-field-${field.key}`}>
      <div className="flex items-center justify-between"><Label className="text-xs">{field.label} ({items.length})</Label>
        <button onClick={() => onChange([...items, {}])} className="text-xs inline-flex items-center gap-1 text-amber-700" data-testid="items-add-btn"><Plus className="w-3 h-3" />Add</button></div>
      <div className="mt-2 space-y-2">
        {items.map((it, i) => (
          <div key={i} className="border border-neutral-200 p-2 space-y-2 bg-neutral-50">
            <div className="flex items-center justify-between text-xs text-neutral-500">
              <span>#{i + 1}</span>
              <div className="flex gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-0.5 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-0.5 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="p-0.5 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {field.item_fields.map((f) => React.createElement(FieldEditor, { key: f.key, api, field: f, value: it[f.key], onChange: (v) => update(i, { [f.key]: v }), compact: true, context }))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const FieldEditor = ({ api, field, value, onChange, compact, pages, onAi, context }) => {
  const label = <div className="flex items-center justify-between"><Label className="text-xs">{field.label}</Label>{onAi && ['text', 'textarea', 'richtext'].includes(field.type) && <button onClick={() => onAi(field)} className="text-[11px] inline-flex items-center gap-1 text-amber-700 hover:underline" data-testid={`ai-field-${field.key}`}><Sparkles className="w-3 h-3" />AI</button>}</div>;
  switch (field.type) {
    case 'textarea':
      return <div>{label}<Textarea rows={compact ? 2 : 4} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} /></div>;
    case 'richtext':
      return <div>{label}<RichTextEditor value={value || ''} onChange={onChange} placeholder="Write content…" className="mt-1" /></div>;
    case 'image':
      return <ImageField api={api} value={value} onChange={onChange} label={field.label} context={context} />;
    case 'button':
      return <ButtonField value={value} onChange={onChange} label={field.label} pages={pages} />;
    case 'items':
      return <ItemsField api={api} field={field} value={value} onChange={onChange} context={context} />;
    case 'icon':
      return <div>{label}<IconField value={value} onChange={onChange} /></div>;
    case 'select':
      return <div>{label}<select className="w-full border border-neutral-300 h-9 text-sm bg-white px-2" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`}><option value="">—</option>{field.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}</select></div>;
    case 'number':
      return <div>{label}<Input type="number" className="h-9" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} data-testid={`field-${field.key}`} /></div>;
    case 'boolean':
      return <div className="flex items-center justify-between"><Label className="text-xs">{field.label}</Label><Switch checked={!!value} onCheckedChange={onChange} /></div>;
    case 'file':
      return <div>{label}<Input className="h-9" placeholder="https://… (file URL)" value={value || ''} onChange={(e) => onChange(e.target.value)} /></div>;
    default:
      return <div>{label}<Input className="h-9" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} /></div>;
  }
};

export const Chip = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`px-2.5 py-1 text-xs border transition-colors ${active ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 hover:border-neutral-500'}`}>{children}</button>
);

export const CloseX = ({ onClick }) => <button onClick={onClick} className="p-1 hover:bg-neutral-100" aria-label="Close"><X className="w-4 h-4" /></button>;
